/**
 * ApiKeyModal - Modal for entering OpenAI API key
 * 
 * Displays when user tries to use AI features without an API key set.
 * Stores the key in the user's Firestore settings.
 */

import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel } from './GlassPanel'
import { Button } from './Button'
import { useUserSettings } from '../../hooks/useUserSettings'
import { Icon } from './Icon'
import './ApiKeyModal.css'

/**
 * ApiKeyModal Component
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal closes
 * @param {function} onSuccess - Callback when API key is successfully saved
 */
export function ApiKeyModal({ isOpen, onClose, onSuccess }) {
  const { setOpenAIApiKey, getOpenAIApiKey, saving } = useUserSettings()
  const [apiKey, setApiKey] = useState('')
  const [error, setError] = useState(null)
  const [showKey, setShowKey] = useState(false)
  const inputRef = useRef(null)

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Pre-fill with existing key if available
      const existingKey = getOpenAIApiKey()
      setApiKey(existingKey || '')
      setError(null)
      setShowKey(false)
    }
  }, [isOpen, getOpenAIApiKey])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const trimmedKey = apiKey.trim()
    
    if (!trimmedKey) {
      setError('Please enter your API key')
      return
    }

    // Basic validation - OpenAI keys start with 'sk-'
    if (!trimmedKey.startsWith('sk-')) {
      setError('API key should start with "sk-"')
      return
    }

    if (trimmedKey.length < 20) {
      setError('API key seems too short')
      return
    }

    setError(null)

    try {
      await setOpenAIApiKey(trimmedKey)
      onSuccess?.()
      onClose()
    } catch (err) {
      console.error('Failed to save API key:', err)
      setError(err.message || 'Failed to save API key')
    }
  }

  const handleClear = async () => {
    if (window.confirm('Remove your API key? AI features will stop working.')) {
      try {
        await setOpenAIApiKey(null)
        setApiKey('')
        onClose()
      } catch (err) {
        console.error('Failed to clear API key:', err)
        setError(err.message || 'Failed to clear API key')
      }
    }
  }

  // Mask API key for display
  const maskKey = (key) => {
    if (!key || key.length < 8) return key
    return key.slice(0, 7) + '...' + key.slice(-4)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="api-key-modal__overlay" onClick={onClose}>
          <motion.div
            className="api-key-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
          >
            <GlassPanel glow="cyan" padding="xl">
              <div className="api-key-modal__header">
                <span className="api-key-modal__icon"><Icon name="key" size={20} /></span>
                <h2 className="api-key-modal__title">OpenAI API Key</h2>
              </div>
              
              <p className="api-key-modal__description">
                Enter your OpenAI API key to enable AI-powered generation features.
                Your key is stored securely and never shared.
              </p>

              <form onSubmit={handleSubmit} className="api-key-modal__form">
                <div className="api-key-modal__field">
                  <label htmlFor="api-key" className="api-key-modal__label">
                    API Key
                  </label>
                  <div className="api-key-modal__input-wrapper">
                    <input
                      ref={inputRef}
                      id="api-key"
                      type={showKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="sk-..."
                      className="api-key-modal__input"
                      disabled={saving}
                      autoComplete="off"
                    />
                    <button
                      type="button"
                      className="api-key-modal__toggle-visibility"
                      onClick={() => setShowKey(!showKey)}
                      title={showKey ? 'Hide key' : 'Show key'}
                    >
                      <Icon name={showKey ? 'eye' : 'eye-off'} size={18} />
                    </button>
                  </div>
                  <p className="api-key-modal__hint">
                    Get your API key from{' '}
                    <a
                      href="https://platform.openai.com/api-keys"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="api-key-modal__link"
                    >
                      platform.openai.com
                    </a>
                  </p>
                </div>

                {error && (
                  <p className="api-key-modal__error">{error}</p>
                )}

                <div className="api-key-modal__actions">
                  {getOpenAIApiKey() && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={handleClear}
                      disabled={saving}
                    >
                      Remove Key
                    </Button>
                  )}
                  <div className="api-key-modal__actions-right">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={onClose}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={saving}
                    >
                      Save Key
                    </Button>
                  </div>
                </div>
              </form>
            </GlassPanel>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default ApiKeyModal
